import { createAsyncThunk } from "@reduxjs/toolkit"
import type { NavigateFunction } from "react-router/dist/lib/hooks"

import { RoutesPaths } from "../../../constants/routes.ts"
import { createHandledError } from "../../../errors/handled-error"
import { reportUnexpectedFailure } from "../../../observability/handled-errors"
import { errorSave } from "../../../redux/errors.slice"
import services from "../../../services/api"
import {
  type RefreshedSession,
  SessionRefreshError,
  refreshSession
} from "../../../services/token-refresh"
import type { AuthResponse } from "../../../services/types.ts"
import { handleGetResponseData } from "../../../utils/handlers/handle-get-response-data"
import {
  buildHandledError,
  rejectWithHandledError
} from "../../../utils/handlers/reject-with-handled-error"
import { resetBranchesState } from "../../Branches/redux/branches.slice"
import { clearInviteUserInfo } from "../../TeamManagementPage/redux/team-members.slice"
import * as authServices from "../api/services"
import { verifyToken as verifyTokenReq } from "../api/services"
import { handlePostLoginNavigation } from "../helpers/handle-post-login-navigation.ts"
import { clearState } from "./auth.slice"
import { type AuthState } from "./auth.type.ts"
import { sessionExpiredMessage } from "./session-expired-message"

const SIGN_IN_FLOW = "auth.signIn"
const SIGN_IN_ENDPOINT = "auth/token/"

const isResponseLike = (
  value: unknown
): value is { ok?: boolean; status?: number } =>
  typeof value === "object" && value !== null && "ok" in value

export const signIn = createAsyncThunk<
  any,
  { data: any; navigate: NavigateFunction }
>("auth/signIn", async (params, { dispatch, rejectWithValue }) => {
  try {
    const { data, navigate } = params
    const { redirect, ...requestData } = data
    const responseData = await services.signIn(requestData)
    const response = handleGetResponseData(responseData)

    if (response && response.access && response.userInfo) {
      // Delay for smooth transition
      await new Promise((resolve) => setTimeout(resolve, 500))

      await handlePostLoginNavigation({
        dispatch,
        navigate,
        userInfo: response.userInfo,
        redirect
      })

      return {
        userInfo: response.userInfo,
        accessToken: response.access,
        refreshToken: response.refresh
      }
    }

    return rejectWithValue(
      buildHandledError(response, {
        flow: SIGN_IN_FLOW,
        endpoint: SIGN_IN_ENDPOINT,
        method: "post",
        code: response?.status === 401 ? "invalid_credentials" : undefined
      })
    )
  } catch (e: any) {
    if (e?.status === 200 && e?.data?.otp_required) {
      return rejectWithValue(e?.data)
    }
    if (isResponseLike(e)) {
      return rejectWithValue(
        buildHandledError(e, {
          flow: SIGN_IN_FLOW,
          endpoint: SIGN_IN_ENDPOINT,
          method: "post"
        })
      )
    }
    reportUnexpectedFailure(e, {
      flow: SIGN_IN_FLOW,
      endpoint: SIGN_IN_ENDPOINT
    })
    return rejectWithValue(
      createHandledError(
        {},
        { flow: SIGN_IN_FLOW, endpoint: SIGN_IN_ENDPOINT, method: "post" }
      )
    )
  }
})

// createAccount
export const createAccount = createAsyncThunk<
  any,
  {
    data: any
    navigate: NavigateFunction
  }
>("auth/createAccount", async (params, { dispatch, rejectWithValue }) => {
  try {
    const { data, navigate } = params
    const responseData = await services.createAccount(data)
    const response = handleGetResponseData(responseData)

    if (response && response.data.access && response.data.user) {
      // Update API tokens
      services.updateAccessToken(response.data.access, response.data.refresh)

      if (data?.invite) {
        dispatch(clearInviteUserInfo())
        navigate(RoutesPaths.home)
      }

      return {
        userInfo: response.data.user,
        accessToken: response.data.access,
        refreshToken: response.data.refresh
      }
    } else if (response?.status === 401) {
      return rejectWithValue(response.data)
    } else if (response?.status === 500) {
      return rejectWithValue("Something went wrong, please try later again")
    } else if (response?.data) {
      return rejectWithValue(response.data)
    } else {
      return rejectWithValue("Something went wrong, please try later again")
    }
  } catch (e) {
    return rejectWithValue(`Unknown createAccountFailure: ${e}`)
  }
})

// resendVerificationLink
export const resendVerificationLink = createAsyncThunk<any, { email: string }>(
  "auth/resendVerificationLink",
  async (data, { rejectWithValue }) => {
    try {
      const responseData = await services.resendVerificationLink(data)
      const response = handleGetResponseData(responseData)

      if (response && response.ok) {
        return true
      } else if (response?.status === 401) {
        return rejectWithValue(null)
      } else if (response?.status === 500) {
        return rejectWithValue("Something went wrong, please try later again")
      } else if (response?.data) {
        return rejectWithValue(response.data)
      } else {
        return rejectWithValue("Something went wrong, please try later again")
      }
    } catch (e) {
      return rejectWithValue(`Unknown error: ${e}`)
    }
  }
)

// logout
export const logout = createAsyncThunk<
  boolean,
  { navigate: NavigateFunction; reason?: string | null }
>(
  "auth/logout",
  async ({ navigate }, { dispatch, getState }) => {
    const auth = (getState() as { auth: AuthState }).auth

    try {
      if (auth?.refreshToken) {
        await services
          .logout({ access: auth.accessToken, refresh: auth.refreshToken })
          .catch(() => null)
      }

      services.updateAccessToken(null, null)
      dispatch(clearState())
      // A different account can sign in on the same browser with no hard
      // reload — reset the branch-scope slice so its in-memory dashboard
      // snapshot/setup status and its persisted global-branch/page-filter
      // selections (which can otherwise rehydrate on next boot before the
      // new account's own accessible-branches fetch validates them) never
      // leak across accounts.
      dispatch(resetBranchesState())

      await new Promise((resolve) => setTimeout(resolve, 100))
      navigate("/login")
      return true
    } catch (e) {
      console.log("error:", e)

      services.updateAccessToken(null, null)
      dispatch(clearState())
      dispatch(resetBranchesState())
      navigate("/login")
      return false
    }
  },
  {
    condition: (_, { getState }) =>
      !(getState() as { auth: AuthState }).auth?.isLogoutPending
  }
)

// confirmEmail
export const confirmEmail = createAsyncThunk<
  any,
  {
    hash: string
    navigate: NavigateFunction
    redirect?: string
  }
>(
  "auth/confirmEmail",
  async ({ hash, navigate, redirect }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(clearState())
      const responseData = await services.confirmEmail(hash)
      const response = handleGetResponseData(responseData)

      if (response && response.access && response.userInfo) {
        navigate("/onboarding/questions")
        return {
          userInfo: response.userInfo,
          accessToken: response.access,
          refreshToken: response.refresh
        }
      } else {
        if (response?.data) {
          if (response.status === 401) {
            dispatch(logout({ navigate }))
          }
          const error =
            response.status === 500
              ? "Something went wrong, please try later again"
              : response.data

          if (redirect) {
            navigate(redirect)
          } else {
            navigate("/sign-up")
          }

          return rejectWithValue(error)
        } else {
          navigate(redirect || "/sign-up")
          return rejectWithValue("Something went wrong, please try later again")
        }
      }
    } catch (e) {
      return rejectWithValue(`Unknown error: ${e}`)
    }
  }
)

// checkPassword
export const checkPassword = createAsyncThunk<
  any,
  { email: string; navigate: NavigateFunction }
>("auth/checkPassword", async ({ email, navigate }, { rejectWithValue }) => {
  try {
    const responseData = await services.checkPassword(email)
    const response = handleGetResponseData(responseData)

    if (response && response.ok) {
      // navigate("/verify-code", email)
      navigate("/verify-code")
      return true
    } else if (response?.status === 401) {
      return rejectWithValue(null)
    } else if (response?.status === 500) {
      return rejectWithValue("Something went wrong, please try later again")
    } else if (response?.data) {
      return rejectWithValue(response.data)
    } else {
      return rejectWithValue("Something went wrong, please try later again")
    }
  } catch (e) {
    return rejectWithValue(`Unknown error: ${e}`)
  }
})

// confirmPassword
export const confirmPassword = createAsyncThunk<
  any,
  { code: string; navigate: NavigateFunction }
>("auth/confirmPassword", async ({ code, navigate }, { rejectWithValue }) => {
  try {
    const responseData = await services.confirmPassword(code)
    const response = handleGetResponseData(responseData)

    if (response && response.access && response.userInfo) {
      navigate("/restore-password/code")
      return {
        accessToken: response.access,
        refreshToken: response.refresh
      }
    } else if (response?.status === 401) {
      return rejectWithValue(null)
    } else if (response?.status === 500) {
      return rejectWithValue("Something went wrong, please try later again")
    } else if (response?.data) {
      return rejectWithValue(response.data)
    } else {
      return rejectWithValue("Something went wrong, please try later again")
    }
  } catch (e) {
    return rejectWithValue(`Unknown error: ${e}`)
  }
})

// createUserPassword
export const createUserPassword = createAsyncThunk<
  any,
  {
    data: { password: string; isNewUserCheck: boolean }
    navigate: NavigateFunction
  }
>(
  "auth/createUserPassword",
  async ({ data, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const { password, isNewUserCheck } = data
      const responseData = await services.createUserPassword(password)
      const response = handleGetResponseData(responseData)

      if (response && response.ok) {
        if (isNewUserCheck) {
          await new Promise((resolve) => setTimeout(resolve, 500))
          navigate("/onboarding/plan")
        } else {
          dispatch(logout({ navigate }))
          navigate("/login")
        }
        return response.data
      } else if (response?.status === 500) {
        return rejectWithValue("Something went wrong, please try later again")
      } else if (response?.data) {
        return rejectWithValue(response.data)
      } else {
        return rejectWithValue("Something went wrong, please try later again")
      }
    } catch (e) {
      return rejectWithValue(`Unknown error: ${e}`)
    }
  }
)

// A refresh that finds no tokens left means the session was already torn down
// (a straggler 401 after logout) — there is nothing to tell the user.
const forcedLogoutReason = (e: unknown): string | null =>
  e instanceof SessionRefreshError && e.reason === "no-session"
    ? null
    : sessionExpiredMessage()

// A backend that never answered cannot have invalidated anything, so the
// session outlives the outage instead of being torn down and blamed on the user
const isBackendUnavailable = (e: unknown): boolean =>
  e instanceof SessionRefreshError && e.reason === "unavailable"

// refreshToken
export const refreshToken = createAsyncThunk<
  RefreshedSession,
  { navigate: NavigateFunction }
>(
  "auth/refreshToken",
  async ({ navigate }, { dispatch, getState, rejectWithValue }) => {
    try {
      const auth = (getState() as { auth: AuthState }).auth
      const session = await refreshSession({
        currentAccessToken: auth?.accessToken ?? null,
        fallbackRefreshToken: auth?.refreshToken ?? null
      })

      services.updateAccessToken(session.access, session.refresh)
      return session
    } catch (e) {
      if (isBackendUnavailable(e)) {
        return rejectWithValue(null)
      }

      const reason = forcedLogoutReason(e)
      dispatch(logout({ navigate, reason }))
      return rejectWithValue(reason)
    }
  }
)

// verifyToken
export const verifyToken = createAsyncThunk<
  any,
  { token: string; navigate: NavigateFunction }
>(
  "auth/verifyToken",
  async ({ token, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const responseData = await verifyTokenReq({ token })
      const response = handleGetResponseData(responseData)

      if (response.status === 200) {
        return response.data
      }

      if ([400, 401, 403, 404].includes(response.status)) {
        return await dispatch(refreshToken({ navigate })).unwrap()
      }

      throw response
    } catch (errors) {
      return rejectWithValue(errors)
    }
  }
)

// socialAuth
export const socialAuth = createAsyncThunk<any, any>(
  "auth/socialAuth",
  async ({ data, navigate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authServices.socialAuth(data)
      const responseData = response?.data

      if (!response.ok) {
        return rejectWithHandledError(response, {
          dispatch,
          rejectWithValue,
          rejectValue: responseData,
          flow: "auth.socialAuth"
        })
      }

      const userData = responseData?.details?.data as AuthResponse | undefined
      await handlePostLoginNavigation({
        dispatch,
        navigate,
        userInfo: userData?.user
      })
      return userData
    } catch (errors) {
      return rejectWithValue(errors)
    }
  }
)

// whatsappLogin
export const whatsappLogin = createAsyncThunk<any, any>(
  "auth/whatsappLogin",
  async (params, { rejectWithValue }) => {
    try {
      const responseData = await authServices.whatsappLogin(params)
      const response = handleGetResponseData(responseData)

      if (response?.ok && response.status === 200) {
        return response?.data
      } else {
        const data = response?.data
        const errors =
          data?.message || data?.message?.detail || "Something went wrong"
        return rejectWithValue(errors)
      }
    } catch (e: any) {
      const data = e?.data
      const errors =
        data?.message || data?.message?.detail || "Something went wrong"
      return rejectWithValue(errors)
    }
  }
)

// whatsappGoogleLogin
export const whatsappGoogleLogin = createAsyncThunk<any, any>(
  "auth/whatsappGoogleLogin",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const responseData = await authServices.socialAuth(data)
      const response = handleGetResponseData(responseData)

      if (response?.ok && response.status === 200) {
        const token = response?.data?.data?.access
        const whatsapp_id = data?.whatsapp_id

        dispatch(whatsappLogin({ token, whatsapp_id }))
        return response?.data
      } else {
        const data = response?.data
        const errors =
          data?.message || data?.message?.detail || "Something went wrong"
        dispatch(errorSave({ errors }))
        return rejectWithValue(errors)
      }
    } catch (e: any) {
      const data = e?.data
      const errors =
        data?.message || data?.message?.detail || "Something went wrong"
      dispatch(errorSave({ errors }))
      return rejectWithValue(errors)
    }
  }
)
